/**
 * pages/FinanceBudgetTab.js — Phase 3
 *
 * Changes from Phase 2:
 *  • ExportButton added to tab header (PDF + Excel)
 *  • Tab content wrapped in <div id="finance-tab-content"> for PDF capture
 *  • Excel sheet definitions derived from live API data
 */
import React, { useState, useEffect } from 'react';
import {
  Grid, Card, CardContent, Typography, Box,
  LinearProgress, CircularProgress, Alert,
} from '@mui/material';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { AccountBalance, CreditCard, TrendingUp, MonetizationOn } from '@mui/icons-material';
import MetricCard          from '../components/MetricCard';
import ExportButton        from '../components/ExportButton';
import BudgetForecastPanel from '../components/BudgetForecastPanel';
import { financeApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

export default function FinanceBudgetTab() {
  const { openWithMessage } = useChatContext();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    financeApi.getSummary()
      .then(setData)
      .catch((e) => setError(e.response?.data?.message || 'Failed to load finance data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const {
    totalWages, medicalClaims, avgSalary, otCosts,
    budgetConsumedPct, annualBudget,
    claimData, earningFunnelData, budgetMonthlyData,
  } = data;
  const maxEarning = earningFunnelData?.[0]?.value || 1;

  // ── Dynamic MetricCard insight / status ──────────────────────────────────────
  const wagesStatus  = budgetConsumedPct == null ? 'blue'
    : budgetConsumedPct < 70 ? 'green'
    : budgetConsumedPct < 90 ? 'yellow'
    : 'red';
  const wagesInsight = budgetConsumedPct == null ? '🔵 Budget data unavailable'
    : budgetConsumedPct < 70 ? `🟢 Within budget (${budgetConsumedPct}% consumed)`
    : budgetConsumedPct < 90 ? `🟡 Budget pressure — ${budgetConsumedPct}% consumed`
    : `🔴 Over-budget risk — ${budgetConsumedPct}% consumed`;

  const medicalPct     = totalWages > 0 ? (medicalClaims / totalWages) * 100 : null;
  const medicalStatus  = medicalPct == null ? 'blue'
    : medicalPct < 3 ? 'green'
    : medicalPct < 5 ? 'yellow'
    : 'red';
  const medicalInsight = medicalPct == null ? '🔵 Data unavailable'
    : medicalPct < 3 ? '🟢 Medical claims within normal range'
    : medicalPct < 5 ? '🟡 Medical claims elevated — monitor trend'
    : `🔴 Alert: Medical claim spike (${medicalPct.toFixed(1)}% of wages)`;

  const salaryInsight = avgSalary == null ? '🔵 Salary data unavailable'
    : `🔵 Avg. monthly salary: RM ${avgSalary?.toLocaleString()}`;

  const otPct     = totalWages > 0 ? (otCosts / totalWages) * 100 : null;
  const otStatus  = otPct == null ? 'blue'
    : otPct < 5 ? 'green'
    : otPct < 10 ? 'yellow'
    : 'red';
  const otInsight = otPct == null ? '🔵 OT data unavailable'
    : otPct < 5 ? `🟢 OT costs under control (${otPct.toFixed(1)}% of wages)`
    : otPct < 10 ? '🟡 OT costs rising — review shift scheduling'
    : `🔴 High OT costs (${otPct.toFixed(1)}% of wages) — action needed`;

  // ── Excel sheet definitions ───────────────────────────────────────────────
  const excelSheets = [
    {
      name: 'KPI Summary',
      data: [{ totalWages, medicalClaims, avgSalary, otCosts, budgetConsumedPct, annualBudget }],
      columns: [
        { key: 'totalWages',         label: 'Total Wages',         format: 'currency', width: 16 },
        { key: 'medicalClaims',      label: 'Medical Claims',      format: 'currency', width: 16 },
        { key: 'avgSalary',          label: 'Avg Salary',          format: 'currency', width: 14 },
        { key: 'otCosts',            label: 'OT Costs',            format: 'currency', width: 12 },
        { key: 'budgetConsumedPct',  label: 'Budget Consumed (%)', format: 'percent',  width: 20 },
        { key: 'annualBudget',       label: 'Annual Budget',       format: 'currency', width: 16 },
      ],
    },
    {
      name: 'Claims Analysis',
      data: claimData || [],
      columns: [
        { key: 'type',   label: 'Claim Type',     width: 16 },
        { key: 'amount', label: 'Actual ($)',      format: 'currency', width: 14 },
        { key: 'avg',    label: 'Seasonal Avg ($)', format: 'currency', width: 18 },
      ],
    },
    {
      name: 'Monthly Payroll',
      data: budgetMonthlyData || [],
      columns: [
        { key: 'month',  label: 'Month',         width: 10 },
        { key: 'actual', label: 'Actual ($)',     format: 'currency', width: 14 },
        { key: 'budget', label: 'Budget Limit ($)', format: 'currency', width: 18 },
      ],
    },
    {
      name: 'Earning Breakdown',
      data: earningFunnelData || [],
      columns: [
        { key: 'name',  label: 'Earning Type', width: 18 },
        { key: 'value', label: 'Amount ($)',    format: 'currency', width: 14 },
      ],
    },
  ];

  return (
    <Box>
      {/* Export bar — sits above the captured content */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ExportButton
          targetId="finance-tab-content"
          filename="finance_budget"
          title="Finance & Budget Report"
          subtitle={`Budget utilisation: ${budgetConsumedPct}% of $${(annualBudget / 1000).toFixed(0)}K`}
          excelSheets={excelSheets}
        />
      </Box>

      {/* ── Captured area ─────────────────────────────────────────────────── */}
      <div id="finance-tab-content">
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} lg={3}>
            {/*<MetricCard title="Total Wages" value={`$${(totalWages / 1000).toFixed(0)}K`} subtitle="Current Month Payroll" icon={AccountBalance} iconColor="#1565C0" insight={`🟢 Within Budget (${budgetConsumedPct}% consumed)`} status="green" />*/}
            <MetricCard title="Total Wages" value={`RM ${totalWages?.toLocaleString()}`} subtitle="Current Month Payroll" icon={AccountBalance} iconColor="#1565C0" insight={wagesInsight} status={wagesStatus}
              onAskAI={() => openWithMessage(`Total payroll this month is RM ${totalWages?.toLocaleString()} (${budgetConsumedPct}% of annual budget consumed). Is this on track and what should we watch?`)} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard title="Medical Claims" value={`RM ${medicalClaims?.toLocaleString()}`} subtitle="Month-to-date" icon={CreditCard} iconColor="#C62828" trend="+12% above seasonal avg." insight={medicalInsight} status={medicalStatus}
              onAskAI={() => openWithMessage(`Medical claims are RM ${medicalClaims?.toLocaleString()} this month, 12% above the seasonal average. What could be driving this spike and what actions should HR take?`)} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard title="Avg. Salary" value={`RM ${avgSalary?.toLocaleString()}`} subtitle="Per employee / month" icon={MonetizationOn} iconColor="#F57F17" insight={salaryInsight} status="blue"
              onAskAI={() => openWithMessage(`Average salary is RM ${avgSalary?.toLocaleString()} per employee per month. How does this compare to market rates and where are the compensation gaps?`)} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard title="OT Costs" value={`RM ${otCosts?.toLocaleString()}`} subtitle="Overtime this month" icon={TrendingUp} iconColor="#00897B" trend="-8% vs Last Month" insight={otInsight} status={otStatus}
              onAskAI={() => openWithMessage(`Overtime costs are RM ${otCosts?.toLocaleString()} this month, down 8% from last month. What is driving the improvement and how can we sustain it?`)} />
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Annual Payroll Budget Utilisation</Typography>
                  {/*<Typography variant="subtitle1" fontWeight={700} color="success.main">{budgetConsumedPct}% of ${(annualBudget / 1000).toFixed(0)}K</Typography>*/}
                  <Typography variant="subtitle1" fontWeight={700} color="success.main">{budgetConsumedPct}% of RM {annualBudget?.toLocaleString()}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={budgetConsumedPct} sx={{ height: 14, borderRadius: 7, bgcolor: '#E3F2FD', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #1565C0, #26C6DA)', borderRadius: 7 } }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  {/*<Typography variant="caption" color="text.secondary">$0</Typography>
                  <Typography variant="caption" color="text.secondary">Remaining: ${((annualBudget * (100 - budgetConsumedPct) / 100) / 1000).toFixed(0)}K</Typography>
                  <Typography variant="caption" color="text.secondary">${(annualBudget / 1000).toFixed(0)}K</Typography>*/}
                  <Typography variant="caption" color="text.secondary">RM 0</Typography>
                  <Typography variant="caption" color="text.secondary">Remaining: RM {((annualBudget * (100 - budgetConsumedPct) / 100) / 1000).toFixed(0)}K</Typography>
                  <Typography variant="caption" color="text.secondary">RM {(annualBudget / 1000).toFixed(0)}K</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ height: 330 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Claim Analysis by Category</Typography>
                <Typography variant="caption" color="text.secondary">Actual vs. seasonal average ($)</Typography>
                <ResponsiveContainer width="100%" height={245}>
                  <BarChart data={claimData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `RM ${(v / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => [`RM ${v.toLocaleString()}`]} />
                    <Legend />
                    <Bar dataKey="amount" name="Actual" fill="#1565C0" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="avg" name="Seasonal Avg." fill="#90CAF9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ height: 330 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Earning Type Breakdown</Typography>
                <Box sx={{ mt: 1 }}>
                  {(earningFunnelData || []).map((item) => (
                    <Box key={item.name} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                        {/*<Typography variant="body2" color="text.secondary">${item.value?.toLocaleString()}</Typography>*/}
                        <Typography variant="body2" color="text.secondary">RM {item.value?.toLocaleString()}</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={Math.round(item.value / maxEarning * 100)} sx={{ height: 28, borderRadius: 4, bgcolor: '#F5F7FA', '& .MuiLinearProgress-bar': { bgcolor: item.fill, borderRadius: 4 } }} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ height: 280 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Monthly Payroll — Actual vs. Budget</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={budgetMonthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `RM ${(v / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => [`RM ${v.toLocaleString()}`]} />
                    <Legend />
                    <Line type="monotone" dataKey="actual" stroke="#1565C0" strokeWidth={2.5} dot={{ r: 4 }} name="Actual Payroll" />
                    <Line type="monotone" dataKey="budget" stroke="#F57F17" strokeWidth={2} strokeDasharray="6 3" dot={false} name="Budget Limit" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <BudgetForecastPanel />
          </Grid>
        </Grid>
      </div>
    </Box>
  );
}
